import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  crearPromocion,
  actualizarPromocion,
  eliminarPromocion,
  obtenerPromocion,
  obtenerSiguienteNumeroPromocion,
  previsualizarPromocion,
  obtenerReservasPendientes,
  asignarReservas,
} from "../../../../services/promocionesService";
import { listarHorariosPorCurso } from "../../../../services/horariosService";
import { showToast } from "../../../../utils/showToast";
import {
  buildPayload,
  enrichPromocionWithCurso,
  extractPromocion,
  initialFormState,
  normalizePromocion,
  validarPromocion,
} from "../adapters/promocionesAdapter";
import usePromocionesList from "./usePromocionesList";
import usePromocionCursos from "./usePromocionCursos";
import usePromocionCiclos from "./usePromocionCiclos";

// Helpers de fecha en "YYYY-MM-DD" sin depender de una libreria (el frontend
// no tiene dayjs como dependencia) — solo para arrastrar el colchon manual
// de fecha_fin en vivo mientras se edita (ver fetchPreview mas abajo).
const parseYmd = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const diffDias = (a, b) => {
  const da = parseYmd(a);
  const db = parseYmd(b);
  if (!da || !db) return 0;
  return Math.round((da - db) / 86400000);
};
const addDias = (value, dias) => {
  const d = parseYmd(value);
  if (!d) return value;
  d.setDate(d.getDate() + dias);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function usePromocionesLogic() {
  const [modal, setModal] = useState("");
  const [promocionSeleccionada, setPromocionSeleccionada] = useState(null);
  const [formNuevo, setFormNuevo] = useState(initialFormState);
  const [formEditar, setFormEditar] = useState(initialFormState);
  const [savingNuevo, setSavingNuevo] = useState(false);
  const [savingEditar, setSavingEditar] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Cola FIFO — reservas pendientes tras crear promoción
  const [colaFifoPromocion, setColaFifoPromocion] = useState(null); // { id, curso_nombre, fecha_inicio }
  const [colaFifoReservas, setColaFifoReservas] = useState([]);    // lista de reservas
  const [colaFifoAsignando, setColaFifoAsignando] = useState(false);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [numeroPromocionLoading, setNumeroPromocionLoading] = useState(false);
  const [numeroPromocionError, setNumeroPromocionError] = useState("");
  const lastNumeroKeyRef = useRef("");
  const [horariosNuevo, setHorariosNuevo] = useState([]);
  const [horariosEditar, setHorariosEditar] = useState([]);
  const [horariosLoadingNuevo, setHorariosLoadingNuevo] = useState(false);
  const [horariosLoadingEditar, setHorariosLoadingEditar] = useState(false);
  const [horariosErrorNuevo, setHorariosErrorNuevo] = useState("");
  const [horariosErrorEditar, setHorariosErrorEditar] = useState("");
  const [previewNuevo, setPreviewNuevo] = useState({ fecha_fin: "", sesiones: [], no_laborables: [] });
  const [previewEditar, setPreviewEditar] = useState({ fecha_fin: "", sesiones: [], no_laborables: [] });
  const [previewLoadingNuevo, setPreviewLoadingNuevo] = useState(false);
  const [previewLoadingEditar, setPreviewLoadingEditar] = useState(false);
  const [previewErrorNuevo, setPreviewErrorNuevo] = useState("");
  const [previewErrorEditar, setPreviewErrorEditar] = useState("");
  const previewKeyNuevoRef = useRef("");
  const previewKeyEditarRef = useRef("");
  // Ultimo fecha_fin CALCULADO (no el manual) que se vio en el preview de
  // edicion — para poder arrastrar el colchon manual por el mismo delta
  // cuando la fecha de inicio cambia en vivo, sin esperar a guardar.
  const lastComputedFechaFinEditarRef = useRef("");

  const list = usePromocionesList();
  const cursosHook = usePromocionCursos();
  const ciclosHook = usePromocionCiclos();
  const {
    ciclos,
    ciclosLoading,
    ciclosError,
    reloadCiclos,
    cursosPorCiclo,
    cursosLoading: cursosCicloLoading,
    loadCursosPorCiclo,
  } = ciclosHook;

  const stats = useMemo(() => [
    { key: "all", label: "Total promociones", value: list.resumen?.total ?? 0, icon: "bi bi-tags", accent: "" },
    { key: "activas", label: "Activas", value: list.resumen?.activas ?? 0, icon: "bi bi-check-circle", accent: "success", filter: { tipo: "activo", valor: true } },
    { key: "en_curso", label: "En curso", value: list.resumen?.enCurso ?? 0, icon: "bi bi-lightning", accent: "info", filter: { tipo: "estado", valor: "en_curso" } },
    { key: "programadas", label: "Programadas", value: list.resumen?.programadas ?? 0, icon: "bi bi-calendar3", accent: "warning", filter: { tipo: "estado", valor: "programado" } },
  ], [list.resumen]);

  useEffect(() => {
    if (formNuevo.ciclo_id) return;
    const activo =
      ciclos.find((c) => c.activo && String(c.estado || "").toLowerCase() === "activo") ||
      ciclos[0];
    if (activo?.id) {
      setFormNuevo((prev) => ({ ...prev, ciclo_id: String(activo.id) }));
    }
  }, [ciclos, formNuevo.ciclo_id]);

  useEffect(() => {
    if (!formNuevo.ciclo_id) return;
    loadCursosPorCiclo(formNuevo.ciclo_id);
  }, [formNuevo.ciclo_id, loadCursosPorCiclo]);

  useEffect(() => {
    if (!formEditar.ciclo_id) return;
    loadCursosPorCiclo(formEditar.ciclo_id);
  }, [formEditar.ciclo_id, loadCursosPorCiclo]);

  const emptyPreview = useMemo(
    () => ({ fecha_fin: "", sesiones: [], no_laborables: [] }),
    []
  );

  const fetchHorarios = useCallback(async (cursoId, setHorarios, setLoading, setError, setForm) => {
    if (!cursoId) {
      setHorarios([]);
      setError("");
      setLoading(false);
      setForm((prev) => ({ ...prev, horario_id: "" }));
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await listarHorariosPorCurso(cursoId);
      const list = Array.isArray(data) ? data : [];
      setHorarios(list);
      setForm((prev) => {
        if (!list.length) return { ...prev, horario_id: "" };
        const exists = list.some((h) => String(h.id) === String(prev.horario_id));
        return { ...prev, horario_id: exists ? prev.horario_id : String(list[0].id) };
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "No se pudieron cargar los horarios.");
      setHorarios([]);
      setForm((prev) => ({ ...prev, horario_id: "" }));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreview = useCallback(
    async (cursoId, horarioId, fechaInicio, setForm, setPreview, setLoading, setError, keyRef, autoSyncFechaFin = true, lastComputedRef = null) => {
      const normalizedCursoId = cursoId ? Number(cursoId) : null;
      const normalizedHorarioId = horarioId ? Number(horarioId) : null;
      const normalizedFechaInicio = fechaInicio || "";

      if (!normalizedCursoId || !normalizedHorarioId || !normalizedFechaInicio) {
        keyRef.current = "";
        setPreview(emptyPreview);
        setError("");
        setLoading(false);
        if (autoSyncFechaFin) {
          setForm((prev) => (prev.fecha_fin ? { ...prev, fecha_fin: "" } : prev));
        }
        if (lastComputedRef) lastComputedRef.current = "";
        return;
      }

      const key = `${normalizedCursoId}-${normalizedHorarioId}-${normalizedFechaInicio}`;
      keyRef.current = key;
      setLoading(true);
      setError("");

      try {
        const data = await previsualizarPromocion({
          cursoId: normalizedCursoId,
          horarioId: normalizedHorarioId,
          fechaInicio: normalizedFechaInicio,
        });
        if (keyRef.current !== key) return;
        const fechaFin = data?.fecha_fin || "";
        setPreview({
          fecha_fin: fechaFin,
          sesiones: Array.isArray(data?.sesiones) ? data.sesiones : [],
          no_laborables: Array.isArray(data?.no_laborables) ? data.no_laborables : [],
        });
        if (autoSyncFechaFin) {
          setForm((prev) =>
            prev.fecha_fin === fechaFin ? prev : { ...prev, fecha_fin: fechaFin }
          );
        } else {
          // Edicion: si el campo tenia un colchon manual (distinto al ultimo
          // fecha_fin calculado que se vio), se arrastra por el mismo delta
          // de dias que se movio el calculo — en vivo, sin esperar a guardar
          // — para que el calendario y el input no se desincronicen mientras
          // se edita (el guardado real repite este mismo criterio en el
          // backend, ver aplicarFechaFinManual en promociones.controller.js).
          const anteriorCalculado = lastComputedRef?.current || "";
          if (anteriorCalculado) {
            setForm((prev) => {
              if (!prev.fecha_fin || prev.fecha_fin === anteriorCalculado) {
                return prev.fecha_fin === fechaFin ? prev : { ...prev, fecha_fin: fechaFin };
              }
              const delta = diffDias(fechaFin, anteriorCalculado);
              if (!delta) return prev;
              const arrastrado = addDias(prev.fecha_fin, delta);
              return prev.fecha_fin === arrastrado ? prev : { ...prev, fecha_fin: arrastrado };
            });
          }
          // Si no hay "anteriorCalculado" (primera vez que corre el preview
          // tras abrir el modal), no se toca form.fecha_fin: puede traer un
          // colchon manual desde la BD que no debe pisarse recien al montar.
        }
        if (lastComputedRef) lastComputedRef.current = fechaFin;
      } catch (err) {
        if (keyRef.current !== key) return;
        console.error(err);
        setError(err.message || "No se pudo calcular la fecha de fin.");
        setPreview(emptyPreview);
        if (autoSyncFechaFin) {
          setForm((prev) => (prev.fecha_fin ? { ...prev, fecha_fin: "" } : prev));
        }
      } finally {
        if (keyRef.current === key) {
          setLoading(false);
        }
      }
    },
    [emptyPreview]
  );

  useEffect(() => {
    fetchHorarios(formNuevo.curso_id, setHorariosNuevo, setHorariosLoadingNuevo, setHorariosErrorNuevo, setFormNuevo);
  }, [formNuevo.curso_id, fetchHorarios]);

  useEffect(() => {
    fetchHorarios(formEditar.curso_id, setHorariosEditar, setHorariosLoadingEditar, setHorariosErrorEditar, setFormEditar);
  }, [formEditar.curso_id, fetchHorarios]);

  useEffect(() => {
    fetchPreview(
      formNuevo.curso_id,
      formNuevo.horario_id,
      formNuevo.fecha_inicio,
      setFormNuevo,
      setPreviewNuevo,
      setPreviewLoadingNuevo,
      setPreviewErrorNuevo,
      previewKeyNuevoRef
    );
  }, [formNuevo.curso_id, formNuevo.horario_id, formNuevo.fecha_inicio, fetchPreview]);

  useEffect(() => {
    // autoSyncFechaFin=false: al editar, la fecha de fin ya viene de la BD (y
    // puede traer un colchon puesto a mano, ej. para cuadrar un minimo de
    // dias); si esto la pisara con el valor recien calculado cada vez que
    // cambia la fecha de inicio, ese colchon se perderia. En su lugar,
    // fetchPreview arrastra el colchon en vivo por el mismo delta de dias
    // (usando lastComputedFechaFinEditarRef como referencia), y el backend
    // repite el mismo criterio al guardar (ver aplicarFechaFinManual).
    fetchPreview(
      formEditar.curso_id,
      formEditar.horario_id,
      formEditar.fecha_inicio,
      setFormEditar,
      setPreviewEditar,
      setPreviewLoadingEditar,
      setPreviewErrorEditar,
      previewKeyEditarRef,
      false,
      lastComputedFechaFinEditarRef
    );
  }, [formEditar.curso_id, formEditar.horario_id, formEditar.fecha_inicio, fetchPreview]);

  const cursosDisponiblesNuevo = useMemo(() => {
    const cicloId = formNuevo.ciclo_id ? String(formNuevo.ciclo_id) : "";
    if (!cicloId) return [];
    return cursosPorCiclo[cicloId] || [];
  }, [formNuevo.ciclo_id, cursosPorCiclo]);

  const cursosDisponiblesEditar = useMemo(() => {
    const cicloId = formEditar.ciclo_id ? String(formEditar.ciclo_id) : "";
    if (!cicloId) return [];
    return cursosPorCiclo[cicloId] || [];
  }, [formEditar.ciclo_id, cursosPorCiclo]);

  const cursosLoadingNuevo =
    Boolean(formNuevo.ciclo_id && cursosCicloLoading[String(formNuevo.ciclo_id)]);

  const cursosLoadingEditar =
    Boolean(formEditar.ciclo_id && cursosCicloLoading[String(formEditar.ciclo_id)]);

  // Limpiar curso seleccionado si ya no está disponible al cambiar de ciclo
  useEffect(() => {
    const cicloId = formNuevo.ciclo_id ? String(formNuevo.ciclo_id) : "";
    if (!cicloId || !formNuevo.curso_id) return;
    const cursos = cursosPorCiclo[cicloId];
    if (!cursos) return;
    const existe = cursos.some((c) => String(c.id) === String(formNuevo.curso_id));
    if (!existe) setFormNuevo((prev) => ({ ...prev, curso_id: "" }));
  }, [formNuevo.ciclo_id, formNuevo.curso_id, cursosPorCiclo]);

  useEffect(() => {
    const cicloId = formEditar.ciclo_id ? String(formEditar.ciclo_id) : "";
    if (!cicloId || !formEditar.curso_id) return;
    const cursos = cursosPorCiclo[cicloId];
    if (!cursos) return;
    const existe = cursos.some((c) => String(c.id) === String(formEditar.curso_id));
    if (!existe) setFormEditar((prev) => ({ ...prev, curso_id: "" }));
  }, [formEditar.ciclo_id, formEditar.curso_id, cursosPorCiclo]);

  const calcularNumeroPromocion = useCallback(async (cicloId, cursoId) => {
    if (!cicloId || !cursoId) {
      lastNumeroKeyRef.current = "";
      setNumeroPromocionLoading(false);
      setNumeroPromocionError("");
      setFormNuevo((prev) => ({ ...prev, numero_promocion: "" }));
      return;
    }

    const key = `${cicloId}-${cursoId}`;
    lastNumeroKeyRef.current = key;
    setNumeroPromocionLoading(true);
    setNumeroPromocionError("");

    try {
      const data = await obtenerSiguienteNumeroPromocion(cicloId, cursoId);
      if (lastNumeroKeyRef.current !== key) return;
      setFormNuevo((prev) => ({ ...prev, numero_promocion: data?.numero_promocion || "" }));
    } catch (err) {
      if (lastNumeroKeyRef.current !== key) return;
      console.error(err);
      setNumeroPromocionError(err.message || "No se pudo calcular el numero de promocion.");
      setFormNuevo((prev) => ({ ...prev, numero_promocion: "" }));
    } finally {
      if (lastNumeroKeyRef.current === key) setNumeroPromocionLoading(false);
    }
  }, []);

  useEffect(() => {
    if (modal !== "nuevo") return;
    calcularNumeroPromocion(formNuevo.ciclo_id, formNuevo.curso_id);
  }, [modal, formNuevo.ciclo_id, formNuevo.curso_id, calcularNumeroPromocion]);

  const reloadNumeroPromocion = useCallback(() => {
    if (modal !== "nuevo") return;
    calcularNumeroPromocion(formNuevo.ciclo_id, formNuevo.curso_id);
  }, [modal, formNuevo.ciclo_id, formNuevo.curso_id, calcularNumeroPromocion]);

  const abrirModalNuevo = useCallback(() => {
    setFormNuevo(initialFormState);
    setPromocionSeleccionada(null);
    setNumeroPromocionError("");
    setNumeroPromocionLoading(false);
    setPreviewNuevo(emptyPreview);
    setPreviewErrorNuevo("");
    setPreviewLoadingNuevo(false);
    lastNumeroKeyRef.current = "";
    previewKeyNuevoRef.current = "";
    setModal("nuevo");
  }, [emptyPreview]);

  const abrirModalEditar = useCallback(
    async (promocion) => {
      if (!promocion?.id) return;
      setModal("editar");
      setDetalleLoading(true);
      previewKeyEditarRef.current = "";
      lastComputedFechaFinEditarRef.current = "";
      try {
        const data = await obtenerPromocion(promocion.id);
        const normalized = normalizePromocion({ ...promocion, ...data });
        const enriched = enrichPromocionWithCurso(normalized, cursosHook.cursos);
        setPromocionSeleccionada(enriched);
        setFormEditar({
          numero_promocion: enriched.nombre,
          ciclo_id: data?.ciclo_id ? String(data.ciclo_id) : "",
          curso_id: enriched.cursoId ? String(enriched.cursoId) : "",
          horario_id: enriched.horarioId ? String(enriched.horarioId) : "",
          fecha_inicio: enriched.fechaInicio || "",
          fecha_fin: enriched.fechaFin || "",
          capacidad: enriched.capacidad ?? 25,
          estado: enriched.estado || "programado",
          descuento_porcentaje: enriched.descuentoPorcentaje ?? "",
          link_whatsapp: enriched.linkWhatsapp || "",
          activo: enriched.activo,
        });
      } catch (err) {
        console.error(err);
        showToast({
          type: "danger",
          message: err.message || "No se pudo obtener la promoción.",
        });
        setModal("");
      } finally {
        setDetalleLoading(false);
      }
    },
    [cursosHook.cursos]
  );

  const abrirModalEliminar = useCallback(
    (promocion) => {
      if (!promocion?.id) return;
      const normalized = normalizePromocion(promocion);
      setPromocionSeleccionada(normalized);
      setModal("eliminar");
    },
    []
  );

  const cerrarModal = useCallback(() => {
    setModal("");
    setPromocionSeleccionada(null);
    setFormNuevo(initialFormState);
    setFormEditar(initialFormState);
    setDetalleLoading(false);
    setNumeroPromocionError("");
    setNumeroPromocionLoading(false);
    lastNumeroKeyRef.current = "";
    setPreviewNuevo(emptyPreview);
    setPreviewEditar(emptyPreview);
    setPreviewErrorNuevo("");
    setPreviewErrorEditar("");
    setPreviewLoadingNuevo(false);
    setPreviewLoadingEditar(false);
    previewKeyNuevoRef.current = "";
    previewKeyEditarRef.current = "";
    lastComputedFechaFinEditarRef.current = "";
  }, [emptyPreview]);

  const handleCrearPromocion = useCallback(
    async (event) => {
      event?.preventDefault?.();
      const validation = validarPromocion(formNuevo);
      if (validation) {
        showToast({ type: "warning", message: validation });
        return;
      }

      setSavingNuevo(true);
      try {
        const payload = buildPayload(formNuevo);
        const creado = await crearPromocion(payload);
        const cursoRelacion =
          cursosDisponiblesNuevo.find(
            (curso) => String(curso.id) === String(payload.curso_id)
          ) || {};
        const fallback = {
          id: creado?.promocion?.id ?? creado?.id,
          numero_promocion: formNuevo.numero_promocion,
          ciclo_id: payload.ciclo_id,
          curso_id: payload.curso_id,
          horario_id: payload.horario_id,
          fecha_inicio: payload.fecha_inicio,
          fecha_fin: payload.fecha_fin,
          capacidad: payload.capacidad ?? 25,
          estado: payload.estado,
          descuento_porcentaje: payload.descuento_porcentaje,
          link_whatsapp: payload.link_whatsapp,
          activo: payload.activo,
          created_at:
            creado?.promocion?.created_at ||
            creado?.created_at ||
            new Date().toISOString(),
          curso: cursoRelacion.nombre || cursoRelacion.nombre_curso,
          centro: cursoRelacion.centroNombre || cursoRelacion.centro,
        };
        const normalized = extractPromocion(creado, fallback);
        list.upsertPromocion(normalized);
        showToast({
          type: "success",
          message: "Promoción creada correctamente.",
        });
        cerrarModal();
        // Verificar cola FIFO de reservas para este curso
        const promoId = creado?.promocion?.id ?? creado?.id;
        if (promoId) {
          obtenerReservasPendientes(promoId)
            .then(data => {
              if (data?.total > 0) {
                setColaFifoPromocion({
                  id: promoId,
                  curso_nombre: fallback.curso || payload.curso_id,
                  fecha_inicio: payload.fecha_inicio,
                });
                setColaFifoReservas(data.reservas || []);
              }
            })
            .catch(() => { /* silencioso — no bloquear flujo normal */ });
        }
      } catch (err) {
        console.error(err);
        showToast({
          type: "danger",
          message: err.message || "No se pudo crear la promoción.",
        });
      } finally {
        setSavingNuevo(false);
      }
    },
    [formNuevo, cerrarModal, cursosDisponiblesNuevo, list]
  );

  const handleActualizarPromocion = useCallback(
    async (event) => {
      event?.preventDefault?.();
      if (!promocionSeleccionada) return;
      const validation = validarPromocion(formEditar);
      if (validation) {
        showToast({ type: "warning", message: validation });
        return;
      }

      setSavingEditar(true);
      try {
        const payload = buildPayload(formEditar);
        const actualizado = await actualizarPromocion(promocionSeleccionada.id, payload);
        const cursoRelacion =
          cursosDisponiblesEditar.find(
            (curso) => String(curso.id) === String(payload.curso_id)
          ) || {};
        const fallback = {
          ...promocionSeleccionada,
          ...payload,
          numero_promocion: formEditar.numero_promocion,
          curso: cursoRelacion.nombre || promocionSeleccionada.cursoNombre,
          centro: cursoRelacion.centroNombre || cursoRelacion.centro || promocionSeleccionada.centroNombre,
          descuento_porcentaje: payload.descuento_porcentaje,
          link_whatsapp: payload.link_whatsapp,
        };
        list.upsertPromocion(extractPromocion(actualizado, fallback));
        if (actualizado?.cronograma_regenerado) {
          showToast({
            type: "warning",
            message:
              "Promoción actualizada. Cambiaste curso u horario, así que el cronograma de sesiones se regeneró: revisa los bloques manuales (materia/docente) del calendario.",
          });
        } else {
          showToast({
            type: "success",
            message: "Promoción actualizada correctamente.",
          });
        }
        cerrarModal();
      } catch (err) {
        console.error(err);
        showToast({
          type: "danger",
          message: err.message || "No se pudo actualizar la promoción.",
        });
      } finally {
        setSavingEditar(false);
      }
    },
    [promocionSeleccionada, formEditar, cerrarModal, cursosDisponiblesEditar, list]
  );

  const handleToggleCancelPromocion = useCallback(
    async (nextEstado) => {
      if (!promocionSeleccionada) return;

      setSavingEditar(true);
      try {
        const payload = buildPayload({ ...formEditar, estado: nextEstado });
        const actualizado = await actualizarPromocion(promocionSeleccionada.id, payload);
        const cursoRelacion =
          cursosDisponiblesEditar.find(
            (curso) => String(curso.id) === String(payload.curso_id)
          ) || {};
        const fallback = {
          ...promocionSeleccionada,
          ...payload,
          numero_promocion: formEditar.numero_promocion,
          curso: cursoRelacion.nombre || promocionSeleccionada.cursoNombre,
          centro: cursoRelacion.centroNombre || cursoRelacion.centro || promocionSeleccionada.centroNombre,
          descuento_porcentaje: payload.descuento_porcentaje,
          link_whatsapp: payload.link_whatsapp,
        };
        const normalized = extractPromocion(actualizado, fallback);
        list.upsertPromocion(normalized);
        setPromocionSeleccionada(normalized);
        setFormEditar((prev) => ({ ...prev, estado: normalized.estado }));
        showToast({
          type: "success",
          message:
            nextEstado === "cancelado"
              ? "Promocion cancelada correctamente."
              : "Promocion reactivada correctamente.",
        });
      } catch (err) {
        console.error(err);
        showToast({
          type: "danger",
          message: err.message || "No se pudo actualizar el estado de la promocion.",
        });
      } finally {
        setSavingEditar(false);
      }
    },
    [promocionSeleccionada, formEditar, cursosDisponiblesEditar, list]
  );

  const handleEliminarPromocion = useCallback(async () => {
    if (!promocionSeleccionada) return;
    setDeleting(true);
    try {
      await eliminarPromocion(promocionSeleccionada.id);
      list.removePromocion(promocionSeleccionada.id);
      showToast({
        type: "success",
        message: "Promoción eliminada correctamente.",
      });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({
        type: "danger",
        message: err.message || "No se pudo eliminar la promoción.",
      });
    } finally {
      setDeleting(false);
    }
  }, [promocionSeleccionada, cerrarModal, list]);

  const cerrarColaFifo = useCallback(() => {
    setColaFifoPromocion(null);
    setColaFifoReservas([]);
  }, []);

  const handleAsignarReservas = useCallback(async (matricula_ids) => {
    if (!colaFifoPromocion) return;
    setColaFifoAsignando(true);
    try {
      const res = await asignarReservas(colaFifoPromocion.id, matricula_ids);
      showToast({ type: "success", message: `${res.asignados} reserva(s) asignada(s). Se enviaron correos de confirmación.` });
      cerrarColaFifo();
      list.reload();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "Error al asignar reservas." });
    } finally {
      setColaFifoAsignando(false);
    }
  }, [colaFifoPromocion, cerrarColaFifo, list]);

  return {
    promociones: list.promocionesFiltradas,
    promocionesPaginadas: list.promocionesPaginadas,
    cursos: cursosHook.cursos,
    cursosDisponiblesNuevo,
    cursosDisponiblesEditar,
    cursosLoadingNuevo,
    cursosLoadingEditar,
    horariosNuevo,
    horariosEditar,
    horariosLoadingNuevo,
    horariosLoadingEditar,
    horariosErrorNuevo,
    horariosErrorEditar,
    previewNuevo,
    previewEditar,
    previewLoadingNuevo,
    previewLoadingEditar,
    previewErrorNuevo,
    previewErrorEditar,
    ciclos,
    ciclosLoading,
    ciclosError,
    numeroPromocionLoading,
    numeroPromocionError,
    loading: list.loading,
    listError: list.listError,
    cursosLoading: cursosHook.cursosLoading,
    cursosError: cursosHook.cursosError,
    resumen: list.resumen,
    stats,
    lastUpdated: list.lastUpdated,
    modal,
    filtro: list.filtro,
    filtros: list.filtros,
    setFiltros: list.setFiltros,
    limpiarFiltros: list.limpiarFiltros,
    page: list.page,
    pageSize: list.pageSize,
    setPageSize: list.setPageSize,
    totalPages: list.totalPages,
    total: list.total,
    goToPage: list.goToPage,
    promocionSeleccionada,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    savingNuevo,
    savingEditar,
    deleting,
    detalleLoading,
    abrirModalNuevo,
    abrirModalEditar,
    abrirModalEliminar,
    cerrarModal,
    reload: list.reload,
    reloadCursos: cursosHook.reloadCursos,
    reloadCiclos,
    reloadNumeroPromocion,
    aplicarFiltro: list.aplicarFiltro,
    handleCrearPromocion,
    handleActualizarPromocion,
    handleEliminarPromocion,
    handleToggleCancelPromocion,
    colaFifoPromocion,
    colaFifoReservas,
    colaFifoAsignando,
    cerrarColaFifo,
    handleAsignarReservas,
  };
}
